import { useEffect, useMemo, useRef, useState } from 'react'
import type { ReactElement } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Checkbox, Spin, Typography } from 'antd'
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  LoadingOutlined,
} from '@ant-design/icons'
import {
  useAdvisoryAiOrgDetails,
  useAdvisoryAiReport,
  useAdvisoryExports,
  useDownloadAdvisoryExportsZipMutation,
  useExportAdvisoryAiReportMutation,
  useMarkExportsDownloadedMutation,
  useRefreshPopups,
  useUserMedia,
} from '@/features/advisory-ai/hooks'
import { isVoiceCloneProcessing } from '@/features/advisory-ai/api'
import { useToast } from '@/contexts/ToastContext'
import { downloadBlob } from '@/lib/download'
import { ApiRequestError } from '@/lib/httpClient'
import type { AdvisoryAiExportFormat, AdvisoryExport } from '@/features/advisory-ai/types'
import LoadingBar from '@/components/LoadingBar'
import { ExportCard } from './ExportCard'
import {
  AdvisoryExportStatus,
  buildBackgroundMessage,
  estimatedGenerationTimeSummary,
  exportOptions,
  toExportStatus,
} from './exportCardConfig'
import { isAdvisoryAccessDenied } from '@/features/advisory-ai/access'

const EXPORT_TYPE_TITLES = new Map(exportOptions.map((o) => [o.id, o.title]))

function parseFilenameFromContentDisposition(headers: Headers, fallback: string): string {
  const cd = headers.get('Content-Disposition')
  if (!cd) return fallback
  const match = /filename[^;=\n]*=(?:"([^"]*)"|([^;\n]*))/i.exec(cd)
  const name = match ? (match[1] || match[2] || '').trim() : ''
  return name || fallback
}

/**
 * Depends on the API listing X-Missing-Exports in Access-Control-Expose-Headers:
 * it is a different origin, so a header left off that list is hidden from us and
 * `headers.get` returns null exactly as if it had never been sent. Every partial
 * zip would then read as complete — no "not ready yet" toast, and exports the
 * server left out reported as downloaded.
 */
function parseMissingExports(value: string | null): AdvisoryAiExportFormat[] {
  if (!value) return []
  return value
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean) as AdvisoryAiExportFormat[]
}

function formatExportTypeList(types: AdvisoryAiExportFormat[]): string {
  const titles = types.map((t) => EXPORT_TYPE_TITLES.get(t) ?? t)
  if (titles.length === 0) return ''
  if (titles.length === 1) return String(titles[0])
  if (titles.length === 2) return `${titles[0]} and ${titles[1]}`
  return `${titles.slice(0, -1).join(', ')}, and ${titles[titles.length - 1]}`
}

const { Text } = Typography
function toSlug(value?: string | null): string {
  const raw = String(value || '')
  const slug = raw
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return slug || 'organisation'
}

function toDateToken(value?: string | null): string {
  const date = value ? new Date(value) : new Date()
  if (Number.isNaN(date.getTime())) return new Date().toISOString().slice(0, 10).replace(/-/g, '')
  return date.toISOString().slice(0, 10).replace(/-/g, '')
}

type GenerateOutputsStepProps = {
  orgId: string
  callId: string
  clientId: string
  onBack: () => void
  pendingExportsRefresh?: boolean
  onExportsRefreshed?: () => void
  /** Where the "Back to …" / "Done" buttons navigate (defaults to the org's meetings list). */
  listHref?: string
  listLabel?: string
}

export function GenerateOutputsStep({
  orgId,
  callId,
  clientId,
  onBack,
  pendingExportsRefresh = false,
  onExportsRefreshed,
  listHref,
  listLabel = 'Back to Meetings',
}: GenerateOutputsStepProps): ReactElement {
  const navigate = useNavigate()
  const meetingsTarget = listHref ?? `/adviceprep/org/${orgId}/calls`
  const { addToast } = useToast()
  const exportMutation = useExportAdvisoryAiReportMutation()
  const downloadZipMutation = useDownloadAdvisoryExportsZipMutation()
  const markDownloadedMutation = useMarkExportsDownloadedMutation()
  const reportQuery = useAdvisoryAiReport(callId, clientId)
  const orgQuery = useAdvisoryAiOrgDetails(orgId, clientId)
  // Combined voice + avatar for the user — gates the video output.
  const userMediaQuery = useUserMedia()
  const userVoice = userMediaQuery.data?.voice ?? null
  const hasAvatar = !!userMediaQuery.data?.avatar?.hasAvatar

  // Video needs an avatar AND a ready voice (not processing / errored). Locked until then.
  // The video export omits voiceCloneId, so the backend narrates with the user's own voice.
  const voiceReady =
    !!userVoice &&
    !isVoiceCloneProcessing(userVoice.status) &&
    !/error|fail/i.test(String(userVoice.status ?? ''))
  const avatarReady = hasAvatar
  const videoLocked = !avatarReady || !voiceReady
  const shouldFetchExports = !!callId && !!clientId
  const [requestedExportTypes, setRequestedExportTypes] = useState<AdvisoryAiExportFormat[]>([])
  const [selectedExportTypes, setSelectedExportTypes] = useState<Set<AdvisoryAiExportFormat>>(new Set())
  const [bulkGenerating, setBulkGenerating] = useState(false)
  const hasRequestedExportsAwaitingVisibility = requestedExportTypes.length > 0
  const exportsQuery = useAdvisoryExports(
    callId,
    clientId,
    shouldFetchExports,
    shouldFetchExports,
    hasRequestedExportsAwaitingVisibility
  )
  // Memoised so it keeps its identity between renders: six hooks below depend on
  // it, and the export-completion effect would otherwise re-diff every render.
  const existingExports = useMemo(() => exportsQuery.data ?? [], [exportsQuery.data])
  const exportByType = useMemo(
    () =>
      new Map<AdvisoryAiExportFormat, AdvisoryExport>(
        existingExports.map((exp) => [String(exp.exportType).toLowerCase() as AdvisoryAiExportFormat, exp])
      ),
    [existingExports]
  )
  const isLoadingExports = exportsQuery.isLoading
  const exportsLoaded = exportsQuery.isSuccess

  /**
   * Pull the pop-up feed when an export finishes, so "Exported" appears while
   * the user is still looking at the screen that caused it rather than up to
   * three minutes later. The server writes the row itself; nothing is reported
   * back to it from here.
   *
   * Only genuine transitions count. The first loaded response is recorded as a
   * baseline and acted on in no way, because otherwise opening this step would
   * treat every export that finished weeks ago as fresh news.
   *
   * An empty first response still counts as that baseline. Waiting for a
   * non-empty one meant the first export a meeting ever produced seeded the
   * baseline itself, so a generation that finished inside one poll — the common
   * case — was swallowed rather than announced.
   */
  const exportStatusBaseline = useRef<Map<string, AdvisoryExportStatus | ''> | null>(null)
  const refreshPopups = useRefreshPopups(clientId)

  /**
   * The baseline belongs to one meeting. This component survives a `callId`
   * change — a route param change is not a remount — so without the reset,
   * meeting B's finished exports all count as fresh against A's baseline.
   */
  useEffect(() => {
    exportStatusBaseline.current = null
  }, [callId])

  useEffect(() => {
    if (!shouldFetchExports || !exportsLoaded) return

    const current = new Map(
      existingExports.map(
        // Keyed on the type when the API omits an id: the adapter defaults a
        // missing id to '', which collapsed every id-less row onto one entry
        // and lost all but the last of their completions.
        (exp): [string, AdvisoryExportStatus | ''] => [
          exp.advisoryExportId || exp.exportType,
          toExportStatus(exp.status),
        ]
      )
    )

    if (exportStatusBaseline.current === null) {
      exportStatusBaseline.current = current
      return
    }

    const previous = exportStatusBaseline.current
    const justCompleted = [...current.entries()]
      .filter(
        ([id, status]) =>
          status === AdvisoryExportStatus.COMPLETED_EXPORT &&
          previous.get(id) !== AdvisoryExportStatus.COMPLETED_EXPORT
      )
      .map(([id]) => id)

    exportStatusBaseline.current = current
    if (justCompleted.length > 0) refreshPopups()
  }, [callId, existingExports, exportsLoaded, refreshPopups, shouldFetchExports])
  const completedExportTypes = useMemo(
    () =>
      new Set<AdvisoryAiExportFormat>(
        existingExports
          .filter((exp) => toExportStatus(exp.status) === AdvisoryExportStatus.COMPLETED_EXPORT)
          .map((exp) => String(exp.exportType).toLowerCase() as AdvisoryAiExportFormat)
      ),
    [existingExports]
  )
  const inProgressExportTypes = useMemo(
    () =>
      new Set<AdvisoryAiExportFormat>(
        existingExports
          .filter((exp) => {
            const status = toExportStatus(exp.status)
            return status === AdvisoryExportStatus.PENDING_EXPORT || status === AdvisoryExportStatus.EXPORTING
          })
          .map((exp) => String(exp.exportType).toLowerCase() as AdvisoryAiExportFormat)
      ),
    [existingExports]
  )
  const outdatedExports = useMemo(
    () =>
      existingExports.filter(
        (exp) =>
          exp.isOutdated && toExportStatus(exp.status) === AdvisoryExportStatus.COMPLETED_EXPORT
      ),
    [existingExports]
  )
  const outdatedTitles = useMemo(() => {
    const titleByType = new Map(exportOptions.map((opt) => [opt.id, opt.title]))
    return outdatedExports.map((exp) => {
      const type = String(exp.exportType).toLowerCase() as AdvisoryAiExportFormat
      return titleByType.get(type) ?? type
    })
  }, [outdatedExports])
  const hasExportInProgress = inProgressExportTypes.size > 0
  // Just-clicked rows count too: the enqueue returns before the next poll confirms
  // them, and without them the panel blinks out between the click and the refetch.
  const generatingTypes = useMemo(() => {
    const types = new Set<AdvisoryAiExportFormat>(inProgressExportTypes)
    requestedExportTypes.forEach((type) => types.add(type))
    return [...types]
  }, [inProgressExportTypes, requestedExportTypes])
  const backgroundMessage = useMemo(() => buildBackgroundMessage(generatingTypes), [generatingTypes])
  const isInitialExportsLoading = shouldFetchExports && isLoadingExports && existingExports.length === 0

  /**
   * Re-reading a list that is already on screen, because an analysis edit
   * invalidated it. The list stays readable and the statuses may still move, so
   * the actions are held until the answer lands — a Download pressed now can
   * hand over a report that is one second from being marked Outdated.
   *
   * Generation polls this same query every 3s, which would otherwise flicker the
   * notice on and off throughout, so an in-flight export takes it out.
   */
  const isRechecking =
    exportsQuery.isFetching && existingExports.length > 0 && generatingTypes.length === 0

  // A row belongs in the "Ready to download" section iff it's completed, fresh, and has a URL.
  // Everything else (never generated, in error, in progress, outdated) belongs in "Not yet generated".
  const isDownloadEligibleType = (type: AdvisoryAiExportFormat): boolean => {
    const item = exportByType.get(type)
    if (!item) return false
    return toExportStatus(item.status) === AdvisoryExportStatus.COMPLETED_EXPORT && !item.isOutdated && !!item.url
  }

  const downloadSectionOptions = useMemo(
    () => exportOptions.filter((o) => isDownloadEligibleType(o.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [exportByType]
  )
  const generateSectionOptions = useMemo(
    () => exportOptions.filter((o) => !isDownloadEligibleType(o.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [exportByType]
  )
  // Within the generate section, anything currently being generated (either
  // confirmed by the backend or just-clicked and awaiting confirmation) can't be ticked.
  const isGeneratingType = (type: AdvisoryAiExportFormat): boolean =>
    inProgressExportTypes.has(type) || requestedExportTypes.includes(type)
  const generateSelectableTypes = useMemo(
    () => generateSectionOptions.map((o) => o.id).filter((t) => !isGeneratingType(t)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [generateSectionOptions, inProgressExportTypes, requestedExportTypes]
  )
  const downloadSelectableTypes = useMemo(
    () => downloadSectionOptions.map((o) => o.id),
    [downloadSectionOptions]
  )

  const selectedForDownload = useMemo(
    () => downloadSelectableTypes.filter((t) => selectedExportTypes.has(t)),
    [downloadSelectableTypes, selectedExportTypes]
  )
  const selectedForGenerate = useMemo(
    () => generateSelectableTypes.filter((t) => selectedExportTypes.has(t)),
    [generateSelectableTypes, selectedExportTypes]
  )

  const allDownloadSelected =
    downloadSelectableTypes.length > 0 && selectedForDownload.length === downloadSelectableTypes.length
  const someDownloadSelected = selectedForDownload.length > 0 && !allDownloadSelected
  const allGenerateSelected =
    generateSelectableTypes.length > 0 && selectedForGenerate.length === generateSelectableTypes.length
  const someGenerateSelected = selectedForGenerate.length > 0 && !allGenerateSelected

  const bulkInProgress = bulkGenerating || downloadZipMutation.isPending
  // Everything the cards and the bulk buttons disable on. Re-checking counts:
  // the statuses on screen are the pre-edit ones until the refetch lands.
  const actionsLocked = bulkInProgress || isRechecking

  // If a row the user had ticked transitions into "generating", drop it from the selection
  // so the bulk count only reflects rows the bulk action can actually touch.
  useEffect(() => {
    if (selectedExportTypes.size === 0) return
    setSelectedExportTypes((prev) => {
      let changed = false
      const next = new Set(prev)
      prev.forEach((t) => {
        if (inProgressExportTypes.has(t) || requestedExportTypes.includes(t)) {
          next.delete(t)
          changed = true
        }
      })
      return changed ? next : prev
    })
  }, [selectedExportTypes, inProgressExportTypes, requestedExportTypes])

  const orgToken = toSlug(orgQuery.data?.name || orgId)
  const meetingDateToken = toDateToken(reportQuery.data?.meetingDate || reportQuery.data?.startDate || reportQuery.data?.createdAt)

  useEffect(() => {
    if (requestedExportTypes.length === 0) return
    setRequestedExportTypes((prev) => {
      const next = prev.filter((type) => {
        const item = exportByType.get(type)
        if (!item) return true
        const status = toExportStatus(item.status)
        if (status === AdvisoryExportStatus.PENDING_EXPORT || status === AdvisoryExportStatus.EXPORTING) return false
        if (status === AdvisoryExportStatus.COMPLETED_EXPORT && !item.isOutdated) return false
        if (status === AdvisoryExportStatus.ERROR) return false
        return true
      })
      return next.length === prev.length ? prev : next
    })
  }, [requestedExportTypes.length, existingExports, exportByType])

  useEffect(() => {
    if (!pendingExportsRefresh || !shouldFetchExports) return
    void exportsQuery.refetch()
    onExportsRefreshed?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingExportsRefresh, shouldFetchExports])

  const handleGenerate = async (exportType: AdvisoryAiExportFormat) => {
    if (exportType === 'video' && videoLocked) {
      addToast('Add an avatar and voice in AdvicePrep Settings to generate video.', 'warning')
      return
    }
    const isBlocked =
      inProgressExportTypes.has(exportType) ||
      requestedExportTypes.includes(exportType)
    if (isBlocked) return

    setRequestedExportTypes((prev) => Array.from(new Set([...prev, exportType])))

    try {
      await exportMutation.mutateAsync({
        reportId: callId,
        clientId,
        exportFormat: exportType,
      })
    } catch (error) {
      addToast(
        error instanceof Error ? error.message : 'Failed to start export generation',
        isAdvisoryAccessDenied(error) ? 'warning' : 'error'
      )
      setRequestedExportTypes((prev) => prev.filter((type) => type !== exportType))
    }
  }

  const handleToggleSelect = (exportType: AdvisoryAiExportFormat, next: boolean) => {
    setSelectedExportTypes((prev) => {
      const updated = new Set(prev)
      if (next) updated.add(exportType)
      else updated.delete(exportType)
      return updated
    })
  }

  const handleToggleSectionAll = (
    sectionTypes: AdvisoryAiExportFormat[],
    next: boolean
  ) => {
    setSelectedExportTypes((prev) => {
      const updated = new Set(prev)
      sectionTypes.forEach((t) => {
        if (next) updated.add(t)
        else updated.delete(t)
      })
      return updated
    })
  }

  const handleBulkGenerate = async () => {
    const types = [...selectedForGenerate]
    if (types.length === 0) return
    setBulkGenerating(true)
    setSelectedExportTypes((prev) => {
      const next = new Set(prev)
      types.forEach((t) => next.delete(t))
      return next
    })
    const failures: AdvisoryAiExportFormat[] = []
    try {
      for (const type of types) {
        if (type === 'video' && videoLocked) continue
        if (inProgressExportTypes.has(type) || requestedExportTypes.includes(type)) continue
        setRequestedExportTypes((prev) => Array.from(new Set([...prev, type])))
        try {
          await exportMutation.mutateAsync({
            reportId: callId,
            clientId,
            exportFormat: type,
          })
        } catch (error) {
          failures.push(type)
          setRequestedExportTypes((prev) => prev.filter((t) => t !== type))
          addToast(
            error instanceof Error ? error.message : `Failed to start ${type} export`,
            'error'
          )
        }
      }
      if (failures.length === 0) {
        addToast(`Started ${types.length} ${types.length === 1 ? 'export' : 'exports'}.`, 'success')
      }
    } finally {
      setBulkGenerating(false)
    }
  }

  /**
   * Tells the server which exports the user just took, which is what the export
   * pop-ups are written from. Ids, not types — an export type can have been
   * generated more than once.
   */
  const reportDownloaded = (exportIds: string[]) => {
    if (exportIds.length === 0) return
    markDownloadedMutation.mutate({ reportId: callId, clientId, exportIds })
  }

  const handleDownload = (exportItem: AdvisoryExport) => {
    if (!exportItem.url) return

    // Plain '_blank' with `opener` cleared afterwards, rather than the 'noopener'
    // feature: that string asks for a popup *window* instead of a tab, and it
    // always returns null — which would leave a blocked download and a delivered
    // one indistinguishable, and we would report both as downloaded.
    const opened = window.open(exportItem.url, '_blank')
    if (!opened) {
      addToast('Your browser blocked the download — allow pop-ups for this site and try again', 'error')
      return
    }
    opened.opener = null

    if (exportItem.advisoryExportId) {
      markDownloadedMutation.mutate({
        reportId: callId,
        clientId,
        exportIds: [exportItem.advisoryExportId],
      })
    }
  }

  const handleBulkDownload = async () => {
    const types = [...selectedForDownload]
    if (types.length === 0) return
    // Snapshot before the await: the exports list polls at 3s, so a regeneration
    // completing mid-download would otherwise swap in a row whose id was never
    // in this zip.
    const idAtRequest = new Map(types.map((t) => [t, exportByType.get(t)?.advisoryExportId ?? '']))
    try {
      const { blob, headers } = await downloadZipMutation.mutateAsync({
        reportId: callId,
        clientId,
        exportTypes: types,
      })
      const fallbackZipName = `Adviceprep-${orgToken}-${meetingDateToken}.zip`
      const zipName = parseFilenameFromContentDisposition(headers, fallbackZipName)
      downloadBlob(blob, zipName)
      setSelectedExportTypes((prev) => {
        const next = new Set(prev)
        types.forEach((t) => next.delete(t))
        return next
      })

      const missing = parseMissingExports(headers.get('X-Missing-Exports'))
      // Only what is actually in the zip — a type the server left out was not
      // downloaded, whatever the user selected.
      reportDownloaded(
        types
          .filter((t) => !missing.includes(t))
          .map((t) => idAtRequest.get(t) ?? '')
          .filter(Boolean)
      )

      if (missing.length > 0) {
        const downloaded = types.length - missing.length
        addToast(
          `${formatExportTypeList(missing)} ${missing.length === 1 ? "isn't" : "aren't"} ready yet — downloaded the other ${downloaded}.`,
          'info'
        )
      }
    } catch (error) {
      if (error instanceof ApiRequestError) {
        if (error.status === 404 && error.payload && typeof error.payload === 'object') {
          const payload = error.payload as { missingExportTypes?: string[] }
          const missing = (payload.missingExportTypes ?? []) as AdvisoryAiExportFormat[]
          if (missing.length > 0) {
            addToast(
              `${formatExportTypeList(missing)} ${missing.length === 1 ? "isn't" : "aren't"} ready yet — generate them first.`,
              'error'
            )
            return
          }
        }
        addToast(
          error.message || 'Failed to download reports',
          isAdvisoryAccessDenied(error) ? 'warning' : 'error'
        )
        return
      }
      addToast(
        error instanceof Error ? error.message : 'Failed to download reports',
        'error'
      )
    }
  }

  const mediaRequirementRow = (
    label: string,
    ready: boolean,
    has: boolean,
    status?: string | null
  ) => {
    if (ready) {
      return (
        <li className="flex items-center gap-2 text-gray-700">
          <CheckCircleOutlined className="text-green-600" /> {label} ready
        </li>
      )
    }
    if (has && isVoiceCloneProcessing(status ?? undefined)) {
      return (
        <li className="flex items-center gap-2 text-gray-700">
          <LoadingOutlined spin /> {label} processing — ready shortly
        </li>
      )
    }
    return (
      <li className="flex items-center gap-2 text-gray-700">
        <CloseCircleOutlined className="text-red-500" /> {label} not set up
      </li>
    )
  }

  const videoLockNotice = (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
      <div className="font-semibold text-amber-900 mb-2">Add an avatar and voice to enable video</div>
      <ul className="list-none pl-0 m-0 space-y-1">
        {mediaRequirementRow('Avatar', avatarReady, hasAvatar, null)}
        {mediaRequirementRow('Voice', voiceReady, !!userVoice, userVoice?.status)}
      </ul>
      <Button
        type="primary"
        className="feature-button-solid mt-3"
        onClick={() => navigate('/adviceprep/settings')}
      >
        Go to AdvicePrep Settings
      </Button>
    </div>
  )

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Generate Meeting Outputs</h2>
        <p className="text-sm text-gray-600 mb-6">
          Choose what deliverables to prepare for this meeting. All outputs use your reviewed analysis.
        </p>
      </div>

      {!isInitialExportsLoading && outdatedExports.length > 0 ? (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
          <ClockCircleOutlined className="text-amber-600 text-xl mt-0.5 flex-shrink-0" />
          <div className="flex-1 text-sm text-amber-900">
            <p className="font-semibold mb-1">A few outputs may be out of date</p>
            <p className="mb-2">
              You've made changes to your AI Analysis &amp; Review since these were generated, so
              they might not reflect your latest insights. Whenever you're ready, regenerate the
              ones below to refresh them:
            </p>
            <ul className="list-disc list-inside space-y-0.5">
              {outdatedTitles.map((title) => (
                <li key={title}>{title}</li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}

      {!isInitialExportsLoading && isRechecking ? (
        <div className="bg-brand-50 border border-brand-200 rounded-lg p-3">
          {/* Deliberately does not claim an edit happened: isFetching carries no
              provenance, so a plain staleness refetch lands here too. */}
          <div className="mb-2 text-xs font-semibold text-brand-800">
            Checking these are up to date…
          </div>
          <LoadingBar indeterminate label="Checking whether the outputs are up to date" />
        </div>
      ) : null}

      {!isInitialExportsLoading && generatingTypes.length > 0 ? (
        <div className="bg-white border border-gray-200 border-l-[3px] border-l-[var(--feature-primary)] rounded-lg p-4 space-y-3">
          {/* Only what is running right now. Exports are generated ad hoc, at different
              times, so there is no batch to count against — anything already finished
              belongs to an earlier action, not to this one. */}
          <span className="font-semibold text-gray-900 block">
            Generating {generatingTypes.length}{' '}
            {generatingTypes.length === 1 ? 'output' : 'outputs'}
          </span>
          <LoadingBar indeterminate trackClassName="h-1.5" label="Generating outputs" />
          <p className="text-sm text-gray-600 mb-0">{backgroundMessage}</p>
        </div>
      ) : null}

      {isInitialExportsLoading ? (
        <div className="flex flex-col items-center justify-center gap-3 py-24 min-h-[400px]">
          <Spin size="large" indicator={<LoadingOutlined spin />} />
          <Text type="secondary">Loading existing outputs…</Text>
        </div>
      ) : (
        // data-tour anchors the assistant walkthroughs — see assistant/mockData.ts
        <div className="space-y-8" data-tour="outputs">
          {downloadSectionOptions.length > 0 && (
            <section className="space-y-4">
              <div className="flex items-center justify-between gap-4 pb-3 border-b border-gray-100 flex-wrap">
                <Checkbox
                  checked={allDownloadSelected}
                  indeterminate={someDownloadSelected}
                  disabled={downloadSelectableTypes.length === 0 || actionsLocked}
                  onChange={(e) => handleToggleSectionAll(downloadSelectableTypes, e.target.checked)}
                >
                  <span className="font-semibold text-gray-900">
                    Ready to download ({downloadSectionOptions.length})
                  </span>
                </Checkbox>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  loading={downloadZipMutation.isPending}
                  disabled={selectedForDownload.length === 0 || bulkGenerating || isRechecking}
                  onClick={() => void handleBulkDownload()}
                  className="feature-button-solid"
                >
                  Download Reports ({selectedForDownload.length})
                </Button>
              </div>
              {downloadSectionOptions.map((option) => (
                <ExportCard
                  key={option.id}
                  option={option}
                  exportItem={exportByType.get(option.id)}
                  requestedExportTypes={requestedExportTypes}
                  completedExportTypes={completedExportTypes}
                  inProgressExportTypes={inProgressExportTypes}
                  hasExportInProgress={hasExportInProgress}
                  orgToken={orgToken}
                  meetingDateToken={meetingDateToken}
                  onGenerate={handleGenerate}
                  onDownload={handleDownload}
                  selected={selectedExportTypes.has(option.id)}
                  selectionDisabled={actionsLocked}
                  onToggleSelect={handleToggleSelect}
                  bulkInProgress={actionsLocked}
                />
              ))}
            </section>
          )}

          {generateSectionOptions.length > 0 && (
            <section className="space-y-4">
              <div className="flex items-center justify-between gap-4 pb-3 border-b border-gray-100 flex-wrap">
                <Checkbox
                  checked={allGenerateSelected}
                  indeterminate={someGenerateSelected}
                  disabled={generateSelectableTypes.length === 0 || actionsLocked}
                  onChange={(e) => handleToggleSectionAll(generateSelectableTypes, e.target.checked)}
                >
                  <span className="font-semibold text-gray-900">
                    Not yet generated ({generateSectionOptions.length})
                  </span>
                </Checkbox>
                <Button
                  type="primary"
                  loading={bulkGenerating}
                  disabled={selectedForGenerate.length === 0 || downloadZipMutation.isPending || isRechecking}
                  onClick={() => void handleBulkGenerate()}
                  className="feature-button-solid"
                >
                  Generate Reports ({selectedForGenerate.length})
                </Button>
              </div>
              {generateSectionOptions.map((option) => {
                const generating = isGeneratingType(option.id)
                const isVideoLocked = option.id === 'video' && videoLocked
                return (
                  <ExportCard
                    key={option.id}
                    option={option}
                    exportItem={exportByType.get(option.id)}
                    requestedExportTypes={requestedExportTypes}
                    completedExportTypes={completedExportTypes}
                    inProgressExportTypes={inProgressExportTypes}
                    hasExportInProgress={hasExportInProgress}
                    orgToken={orgToken}
                    meetingDateToken={meetingDateToken}
                    onGenerate={handleGenerate}
                    onDownload={handleDownload}
                    selected={selectedExportTypes.has(option.id)}
                    selectionDisabled={generating || actionsLocked || isVideoLocked}
                    selectionDisabledReason={
                      generating
                        ? 'This output is currently generating.'
                        : isVideoLocked
                          ? 'Add an avatar and voice in AdvicePrep Settings to enable video.'
                          : undefined
                    }
                    onToggleSelect={handleToggleSelect}
                    bulkInProgress={actionsLocked}
                    locked={isVideoLocked}
                    lockedNotice={option.id === 'video' ? videoLockNotice : undefined}
                  />
                )
              })}
            </section>
          )}
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <Text className="text-sm text-blue-900">{estimatedGenerationTimeSummary}</Text>
      </div>

      <div className="flex flex-wrap justify-between items-center gap-4 pt-6 border-t border-gray-200 mt-6">
        <div className="flex gap-2">
          <Button onClick={onBack} disabled={bulkInProgress}>← Back</Button>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => navigate(meetingsTarget)} disabled={bulkInProgress}>
            {listLabel}
          </Button>
          <Button
            type="primary"
            onClick={() => navigate(meetingsTarget)}
            disabled={bulkInProgress}
            className="feature-button-solid"
          >
            Done
          </Button>
        </div>
      </div>
    </div>
  )
}
